/**
 * Transfer Network Settings — ADI opt-in + preferences.
 *
 * Phase 1 of the Transfer Network feature. Instructors toggle Join, set
 * their home postcode + radius, pick which transmissions + learner types
 * they're willing to take, and add an availability blurb. Calm, positive,
 * one-page form.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Handshake, Pause, Play, Save } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_SETTINGS = {
  joined: false,
  paused: false,
  postcode: "",
  travel_radius_miles: 10,
  manual_review: false,
  transmissions_accepted: ["manual"],
  accepts_beginners: true,
  accepts_nervous: true,
  accepts_test_ready: true,
  accepts_relocation: true,
  availability_note: "",
};

const TRANSMISSIONS = [
  { value: "manual", label: "Manual" },
  { value: "automatic", label: "Automatic" },
  { value: "either", label: "Either" },
];

const LEARNER_TYPE_FIELDS = [
  {
    key: "accepts_beginners",
    label: "Beginners",
    description: "Learners starting their journey",
  },
  {
    key: "accepts_nervous",
    label: "Nervous learners",
    description: "Extra patience, slower pace",
  },
  {
    key: "accepts_test_ready",
    label: "Test-ready",
    description: "Refresher lessons, mock tests, test-day support",
  },
  {
    key: "accepts_relocation",
    label: "Relocation learners",
    description: "Moved to a new area mid-journey",
  },
];

export default function TransferNetworkSettings() {
  const navigate = useNavigate();
  const goBack = useSafeBack();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/transfer-network/my-settings`,
        { withCredentials: true },
      );
      setSettings({ ...DEFAULT_SETTINGS, ...data });
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("Only instructors can join the Transfer Network");
        navigate("/");
        return;
      }
      toast.error("Couldn't load your settings");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const toggleTransmission = (value) => {
    setSettings((s) => {
      const has = s.transmissions_accepted.includes(value);
      const next = has
        ? s.transmissions_accepted.filter((t) => t !== value)
        : [...s.transmissions_accepted, value];
      return { ...s, transmissions_accepted: next.length ? next : ["manual"] };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/transfer-network/my-settings`,
        settings,
        { withCredentials: true },
      );
      toast.success("Saved — your Transfer Network preferences are live");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save — check the fields");
    } finally {
      setSaving(false);
    }
  };

  const togglePause = async () => {
    const endpoint = settings.paused ? "resume" : "pause";
    try {
      await axios.post(
        `${API_URL}/api/transfer-network/${endpoint}`,
        {},
        { withCredentials: true },
      );
      toast.success(settings.paused ? "Resumed — opportunities will arrive again" : "Paused — no new opportunities for now");
      load();
    } catch {
      toast.error("Couldn't update");
    }
  };

  if (loading) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex items-center justify-center">
        <p className="text-sm text-slate-400">Loading…</p>
      </div>
    );
  }

  const isLive = settings.joined && !settings.paused;

  return (
    <div data-testid="transfer-network-settings-page" className="min-h-dvh no-overflow-x bg-slate-50">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => goBack()}
            className="p-2 rounded-xl hover:bg-slate-100"
            data-testid="transfer-settings-back"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-slate-900">Transfer Network</h1>
            <p className="text-[11px] text-slate-500">
              Opt in to receive calm, positive transfer opportunities in your area
            </p>
          </div>
          {isLive && (
            <span
              data-testid="transfer-status-live"
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 rounded-full px-2 py-1"
            >
              Live
            </span>
          )}
          {settings.joined && settings.paused && (
            <span
              data-testid="transfer-status-paused"
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 rounded-full px-2 py-1"
            >
              Paused
            </span>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        {/* Hero / Join toggle */}
        <div className="bg-gradient-to-br from-indigo-600 to-violet-700 rounded-2xl p-5 text-white">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 bg-white/15 rounded-xl flex items-center justify-center flex-shrink-0">
              <Handshake className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-100">
                {isLive ? "You're in the network" : "Join Transfer Network"}
              </p>
              <h2 className="text-base font-bold mt-0.5">
                Help learners keep their momentum when life changes
              </h2>
              <p className="text-xs text-indigo-100 mt-1 leading-relaxed">
                When a learner moves or needs a different fit, their progress comes with
                them. You'll only receive opportunities that match the transmissions,
                areas and learner types you choose.
              </p>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {!settings.joined ? (
                  <button
                    type="button"
                    onClick={() => setSettings((s) => ({ ...s, joined: true }))}
                    data-testid="transfer-join-btn"
                    className="inline-flex items-center gap-1.5 bg-white text-indigo-700 text-xs font-bold rounded-lg px-3 py-2 hover:bg-indigo-50"
                  >
                    Join Transfer Network
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={togglePause}
                    data-testid="transfer-pause-toggle"
                    className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 text-white text-xs font-bold rounded-lg px-3 py-2"
                  >
                    {settings.paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                    {settings.paused ? "Resume" : "Pause opportunities"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Postcode + radius */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3">
            Area you cover
          </p>
          <div className="flex gap-2">
            <label className="flex-1 text-xs text-slate-600">
              Home postcode
              <input
                type="text"
                value={settings.postcode || ""}
                onChange={(e) => setSettings({ ...settings, postcode: e.target.value.toUpperCase() })}
                placeholder="e.g. M1 or SW1A"
                maxLength={12}
                data-testid="transfer-postcode"
                className="w-full mt-1 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </label>
            <label className="w-28 text-xs text-slate-600">
              Radius (mi)
              <input
                type="number"
                min={1}
                max={50}
                value={settings.travel_radius_miles}
                onChange={(e) => setSettings({ ...settings, travel_radius_miles: Number(e.target.value) || 10 })}
                data-testid="transfer-radius"
                className="w-full mt-1 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </label>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">
            We match on postcode outward code first, then broader area.
          </p>
        </div>

        {/* Transmissions */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3">
            Transmissions you teach
          </p>
          <div className="flex flex-wrap gap-2">
            {TRANSMISSIONS.map((t) => {
              const active = settings.transmissions_accepted.includes(t.value);
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => toggleTransmission(t.value)}
                  data-testid={`transfer-transmission-${t.value}`}
                  className={`text-xs font-semibold rounded-full px-4 py-2 border transition-colors ${
                    active
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Learner types */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3">
            Learner types you'll accept
          </p>
          <div className="space-y-2">
            {LEARNER_TYPE_FIELDS.map((f) => (
              <label
                key={f.key}
                className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={!!settings[f.key]}
                  onChange={(e) => setSettings({ ...settings, [f.key]: e.target.checked })}
                  data-testid={`transfer-${f.key.replace(/_/g, "-")}`}
                  className="mt-1 w-4 h-4 accent-indigo-600"
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-900">{f.label}</p>
                  <p className="text-[11px] text-slate-500">{f.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Availability note */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <label className="block">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
              Availability note (optional)
            </p>
            <textarea
              value={settings.availability_note || ""}
              onChange={(e) => setSettings({ ...settings, availability_note: e.target.value })}
              maxLength={280}
              rows={3}
              placeholder="Weekday evenings + Saturday mornings"
              data-testid="transfer-availability"
              className="w-full text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              {(settings.availability_note || "").length}/280
            </p>
          </label>
        </div>

        {/* Review mode */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!settings.manual_review}
              onChange={(e) => setSettings({ ...settings, manual_review: e.target.checked })}
              data-testid="transfer-manual-review"
              className="mt-1 w-4 h-4 accent-indigo-600"
            />
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-900">Review each opportunity manually</p>
              <p className="text-[11px] text-slate-500">
                When on, opportunities land in your inbox but you won't get push nudges.
                Turn off for faster matches.
              </p>
            </div>
          </label>
        </div>

        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="transfer-save-btn"
          className="w-full inline-flex items-center justify-center gap-2 bg-indigo-600 text-white font-bold text-sm rounded-xl px-4 py-3 hover:bg-indigo-700 disabled:bg-indigo-300"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving…" : "Save preferences"}
        </button>
      </div>
    </div>
  );
}
